/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { Undefined } from '../../helpers/jsHelpers/types';
import type { BASE_VOD_PURCHASE_DATA_TYPE } from '../../helpers/rights/pendingOperations';
import type { NETGEM_API_V8_RAW_FEED } from '../../libs/netgemLibrary/v8/types/FeedResult';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_PURCHASE_INFO, NETGEM_API_V8_PURCHASE_INFO_ITEM, NETGEM_API_V8_PURCHASE_INFO_PRICE_OPTION } from '../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import AccurateTimestamp from '../../helpers/dateTime/AccurateTimestamp';
import { formatAvailabilityTimestamp } from '../../helpers/dateTime/Format';
import { showDebug } from '../../helpers/debug/debug';
import Localizer from '../../helpers/localization';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { ParentalControlLockState, type ParentalControlPurchaseState } from '../../helpers/parentalControl/constsAndTypes';
import ParentalControlManager from '../../helpers/parentalControl/parentalControlManager';
import { Currency, Definition, type PURCHASE_DETAILS_TYPE } from '../../helpers/ui/metadata/Types';
import { Theme, type ThemeType } from '../../helpers/ui/theme';
import {
  BOVodAssetStatus,
  formatDisplayPrice,
  getAvailabilityStartTimeOrDefault,
  getCurrentOrNextPriceOption,
  getPrice,
  getTimeLeftFromExpirationTime,
  isBetterPriceOption,
  renderPurchaseSummary,
} from '../../helpers/videofutur/metadata';
import { getFirstEpisodeLocation, isPackPurchased } from '../../helpers/videofutur/pack';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import { showPinModal } from '../../redux/modal/actions';
import { type BO_PURCHASE_LIST_TYPE, Purchase, type PurchaseType } from '../../redux/netgemApi/actions/videofutur/types/purchase';
import AccessibleButton from '../buttons/AccessibleButton';
import ButtonFXBicolor from '../buttons/ButtonFXBicolor';
import { HeightKind, WidthKind } from '../buttons/types';
import { type PIN_DATA_MODAL_TYPE, PinModalKind } from '../modal/pinModal/PinModalConstsAndTypes';
import './PricingVod.css';

export enum ContextKind {
  Card,
  LandscapeTile,
  RegularTile,
  VodEpisode,
}

type AllPurchaseDetails = {
  [PurchaseType]: PURCHASE_DETAILS_TYPE | null,
};

type DefaultProps = {
  +expirationTime?: number,
  +isVodSeries?: boolean,
  +purchaseInfo?: NETGEM_API_V8_PURCHASE_INFO | null,
  +theme?: ThemeType,
};

type PricingVodPropType = {
  ...DefaultProps,
  +context: ContextKind,
  +locationsMetadata: ?Array<NETGEM_API_V8_METADATA_SCHEDULE>,
  +onClick: (data: BASE_VOD_PURCHASE_DATA_TYPE) => void,
  +status: BOVodAssetStatus,
};

type ReduxPricingVodReducerStateType = {
  +hideVodPreviewDates: boolean,
  +isDebugModeEnabled: boolean,
  +purchaseList: BO_PURCHASE_LIST_TYPE | null,
};

type ReduxPricingVodDispatchToPropsType = {
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
};

type CompletePricingVodPropType = {
  ...PricingVodPropType,
  ...ReduxPricingVodReducerStateType,
  ...ReduxPricingVodDispatchToPropsType,
};

type PricingVodStateType = {
  minEpisodePrice: ?string,
  purchaseDetails: AllPurchaseDetails,
  purchaseLockState: ParentalControlPurchaseState,
};

const InitialState: Readonly<PricingVodStateType> = Object.freeze({
  minEpisodePrice: null,
  purchaseDetails: {
    [Purchase.Buy]: null,
    [Purchase.BuyPack]: null,
    [Purchase.Rent]: null,
  },
  purchaseLockState: {
    hasPinCode: false,
    lockState: ParentalControlLockState.NotInitialized,
  },
});

class PricingVodView extends React.PureComponent<CompletePricingVodPropType, PricingVodStateType> {
  static defaultProps: DefaultProps = {
    expirationTime: 0,
    isVodSeries: false,
    purchaseInfo: null,
    theme: Theme.Dark,
  };

  constructor(props: CompletePricingVodPropType) {
    super(props);

    this.state = {
      ...InitialState,
      purchaseLockState: ParentalControlManager.getPurchaseState(),
    };
  }

  componentDidMount() {
    const { locationsMetadata, purchaseInfo } = this.props;

    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.updateLockState();

    if (locationsMetadata || purchaseInfo) {
      this.updatePurchaseDetails();
    }
  }

  componentDidUpdate(prevProps: CompletePricingVodPropType) {
    const { locationsMetadata, purchaseInfo } = this.props;
    const { locationsMetadata: prevLocationsMetadata, purchaseInfo: prevPurchaseInfo } = prevProps;

    if ((locationsMetadata && locationsMetadata !== prevLocationsMetadata) || (purchaseInfo && purchaseInfo !== prevPurchaseInfo)) {
      this.updatePurchaseDetails();
    }
  }

  componentWillUnmount() {
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);
  }

  updateLockState: () => void = () => {
    this.setState({ purchaseLockState: ParentalControlManager.getPurchaseState() });
  };

  handleOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    const { isDebugModeEnabled } = this.props;
    const { altKey, ctrlKey } = event;

    if (isDebugModeEnabled && (altKey || ctrlKey)) {
      event.preventDefault();
      event.stopPropagation();
      this.showDebugInfo();
    }
  };

  showDebugInfo = () => {
    const { props, state } = this;

    showDebug('Pricing VOD', {
      props,
      propsFields: ['context', 'expirationTime', 'isVodSeries', 'locationsMetadata', 'purchaseInfo', 'status', 'theme'],
      state,
      stateFields: ['minEpisodePrice', 'purchaseDetails'],
    });
  };

  countEpisodes = (elements: NETGEM_API_V8_RAW_FEED): number => elements[0].elements?.length ?? 0;

  getLocationId = (elements: NETGEM_API_V8_RAW_FEED): Undefined<string> => (elements[0].elements?.[0] ?? elements[0]).locations?.[0].id;

  // Pack purchase
  setPackPurchaseDetails = (): void => {
    const { locationsMetadata, purchaseInfo } = this.props;

    const minEpisodePrice = this.getMinEpisodePrice();

    if (!purchaseInfo) {
      this.setState({ minEpisodePrice });
      return;
    }

    let selectedPurchase: NETGEM_API_V8_PURCHASE_INFO_ITEM | null = null;
    let selectedPriceOption: NETGEM_API_V8_PURCHASE_INFO_PRICE_OPTION | null = null;

    purchaseInfo.forEach((purchase) => {
      const { priceOptions } = purchase;
      const priceOption = getCurrentOrNextPriceOption(priceOptions);

      if (priceOption !== null && (selectedPurchase === null || selectedPriceOption === null || isBetterPriceOption(selectedPriceOption, priceOption))) {
        selectedPurchase = purchase;
        selectedPriceOption = priceOption;
      }
    });

    if (selectedPurchase === null || selectedPriceOption === null) {
      // No current or next purchase window
      return;
    }

    const {
      condition: { definition },
      elements,
      settings: { distributorId, vtiId },
    } = selectedPurchase;
    const { availabilityStartDate, display } = selectedPriceOption;
    const availabilityStartTime = getAvailabilityStartTimeOrDefault(availabilityStartDate);
    const displayPrice = formatDisplayPrice(display, Currency.Euro);
    const locationId = getFirstEpisodeLocation(elements)?.locations?.[0].id;
    const vodLocationMetadata = locationsMetadata?.find((loc) => loc.location.id === locationId)?.location;
    const itemCount = this.countEpisodes(elements);

    this.setState({
      purchaseDetails: {
        [Purchase.Buy]: null,
        [Purchase.BuyPack]: {
          availabilityStartTime,
          definition,
          displayPrice,
          distributorId,
          itemCount,
          locationId,
          vodLocationMetadata,
          vtiId,
        },
        [Purchase.Rent]: null,
      },
    });
  };

  // Single item buy/rent
  setPurchaseDetails = (): void => {
    const { locationsMetadata, purchaseInfo } = this.props;

    if (!purchaseInfo) {
      return;
    }

    const selection: { [PurchaseType]: { priceOption: NETGEM_API_V8_PURCHASE_INFO_PRICE_OPTION, purchase: NETGEM_API_V8_PURCHASE_INFO_ITEM } | null } = {
      [Purchase.Buy]: null,
      [Purchase.Rent]: null,
    };

    purchaseInfo.forEach((purchase) => {
      const { condition, priceOptions } = purchase;
      const { licenseDuration } = condition;
      const purchaseType = licenseDuration === undefined ? Purchase.Buy : Purchase.Rent;
      const priceOption = getCurrentOrNextPriceOption(priceOptions);
      const selectedPurchase = selection[purchaseType];

      if (priceOption !== null && (selectedPurchase === null || isBetterPriceOption(selectedPurchase.priceOption, priceOption))) {
        selection[purchaseType] = { priceOption, purchase };
      }
    });

    const buildPurchaseDetails = (purchaseType: PurchaseType, vodLocationsMetadata: ?Array<NETGEM_API_V8_METADATA_SCHEDULE>): PURCHASE_DETAILS_TYPE | null => {
      const { [purchaseType]: selectedPurchase } = selection;

      if (!selectedPurchase) {
        return null;
      }

      const { priceOption, purchase } = selectedPurchase;
      const {
        condition: { definition, licenseDuration },
        elements,
        settings: { distributorId, vtiId },
      } = purchase;
      const { availabilityStartDate, display } = priceOption;
      const availabilityStartTime = getAvailabilityStartTimeOrDefault(availabilityStartDate);
      const displayPrice = formatDisplayPrice(display, Currency.Euro);
      const locationId = this.getLocationId(elements);
      const vodLocationMetadata = vodLocationsMetadata?.find((loc) => loc.location.id === locationId)?.location;

      return {
        availabilityStartTime,
        definition,
        displayPrice,
        distributorId,
        itemCount: 1,
        licenseDuration,
        locationId,
        vodLocationMetadata,
        vtiId,
      };
    };

    this.setState({
      purchaseDetails: {
        [Purchase.Buy]: buildPurchaseDetails(Purchase.Buy, locationsMetadata),
        [Purchase.BuyPack]: null,
        [Purchase.Rent]: buildPurchaseDetails(Purchase.Rent, locationsMetadata),
      },
    });
  };

  updatePurchaseDetails = () => {
    const { isVodSeries } = this.props;

    if (isVodSeries) {
      this.setPackPurchaseDetails();
    } else {
      this.setPurchaseDetails();
    }
  };

  getMinEpisodePrice = (): string | null => {
    const { locationsMetadata } = this.props;

    if (!locationsMetadata) {
      return null;
    }

    let definition: Definition | null = null;
    let minDisplayPrice: string | null = null;
    let minPrice = Number.MAX_SAFE_INTEGER;

    locationsMetadata.forEach((metadata) => {
      const {
        location: { purchaseInfo },
      } = metadata;

      if (purchaseInfo) {
        const price = getPrice(purchaseInfo, Currency.Euro);
        if (price !== null) {
          const { amount, display } = price;
          if (amount < minPrice) {
            ({ definition } = purchaseInfo);
            minDisplayPrice = display;
            minPrice = amount;
          }
        }
      }
    });

    if (minPrice === Number.MAX_SAFE_INTEGER || !minDisplayPrice) {
      return null;
    }

    return Localizer.localize('vod.pricing.episode_min_price', {
      definition: definition !== null ? (definition as string) : '',
      price: formatDisplayPrice(minDisplayPrice, Currency.Euro),
    });
  };

  handleOnBuyClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>): void => {
    const {
      purchaseDetails: { [Purchase.BuyPack]: packPurchaseDetails },
    } = this.state;

    this.onPurchaseClick(event, packPurchaseDetails ? Purchase.BuyPack : Purchase.Buy);
  };

  handleOnRentClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>): void => this.onPurchaseClick(event, Purchase.Rent);

  onPurchaseClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>, purchaseType: PurchaseType) => {
    const { isDebugModeEnabled, localShowPinModal, onClick } = this.props;
    const {
      purchaseDetails,
      purchaseLockState: { hasPinCode, lockState },
    } = this.state;
    const { altKey, ctrlKey } = event;

    if (isDebugModeEnabled && (altKey || ctrlKey)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (lockState === ParentalControlLockState.NotInitialized) {
      return;
    }

    const localOnClick = (isValidPin: boolean): void => {
      if (!isValidPin) {
        return;
      }

      const { [purchaseType]: details } = purchaseDetails;

      // Needed to forbid buying (resp. renting) when item is already rented (resp. bought)
      let otherVtiId: Undefined<number> = undefined;
      if (purchaseType !== Purchase.BuyPack) {
        otherVtiId = purchaseDetails[purchaseType === Purchase.Buy ? Purchase.Rent : Purchase.Buy]?.vtiId;
      }
      if (details) {
        const { definition, displayPrice, distributorId, itemCount, licenseDuration, locationId, vodLocationMetadata, vtiId } = details;

        onClick({
          definition,
          displayPrice,
          distributorId,
          itemCount,
          licenseDuration,
          locationId,
          otherVtiId,
          purchaseType,
          vodLocationMetadata,
          vtiId,
        });
      }
    };

    if (lockState === ParentalControlLockState.Locked) {
      // Purchase locked by parental control
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, localOnClick);
        localShowPinModal({ kind: PinModalKind.UnlockPurchase });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    localOnClick(true);
  };

  getDisplayData = (
    purchaseType: PurchaseType,
    clickCallback: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
  ): {
    element: React.Node,
    isTextOnly: boolean,
  } => {
    const { context, hideVodPreviewDates, theme } = this.props;
    const {
      purchaseDetails: { [purchaseType]: details },
    } = this.state;

    if (!details) {
      return { element: null, isTextOnly: false };
    }

    const { availabilityStartTime, definition, displayPrice } = details;

    if (availabilityStartTime > AccurateTimestamp.now()) {
      // Not yet open to this kind of purchase (BUY or RENT)
      if (hideVodPreviewDates) {
        return { element: null, isTextOnly: false };
      }

      // Show future availability date
      const localizationKey = purchaseType === Purchase.Rent ? 'vod.pricing.availability.tvod' : 'vod.pricing.availability.est';
      return { element: <div className='availabilityDate'>{Localizer.localize(localizationKey, { date: formatAvailabilityTimestamp(availabilityStartTime) })}</div>, isTextOnly: true };
    }

    if (context === ContextKind.VodEpisode) {
      // VOD episode: display rent/buy actions as links
      return {
        element: (
          <AccessibleButton className='pricingLink' onClick={clickCallback}>
            {renderPurchaseSummary(purchaseType, definition, displayPrice)}
          </AccessibleButton>
        ),
        isTextOnly: true,
      };
    }

    // Regular case: display rent/buy actions as buttons
    return {
      element: (
        <ButtonFXBicolor
          heightKind={context === ContextKind.Card ? HeightKind.Large : HeightKind.Medium}
          leftPart={`${Localizer.localize(purchaseType === Purchase.Rent ? 'vod.pricing.actions.rent' : 'vod.pricing.actions.buy')} ${definition as string}`}
          onClick={clickCallback}
          rightPart={displayPrice}
          theme={theme}
          widthKind={context === ContextKind.LandscapeTile ? WidthKind.Large : WidthKind.Stretched}
        />
      ),
      isTextOnly: false,
    };
  };

  renderPackPurchaseButton = (context: ContextKind): React.Node => {
    const { theme } = this.props;
    const {
      purchaseDetails: { [Purchase.BuyPack]: packPurchaseDetails },
    } = this.state;

    if (packPurchaseDetails === null) {
      return null;
    }

    const { availabilityStartTime, definition, displayPrice } = packPurchaseDetails;

    if (availabilityStartTime > AccurateTimestamp.now()) {
      // Not yet open to purchase
      return <div className='availabilityDate'>{Localizer.localize('vod.pricing.availability.est', { date: formatAvailabilityTimestamp(availabilityStartTime) })}</div>;
    }

    return (
      <div className='pricing purchasable spaced' onClick={this.handleOnClick}>
        <ButtonFXBicolor
          heightKind={context === ContextKind.Card ? HeightKind.Large : HeightKind.Medium}
          leftPart={`${Localizer.localize('vod.pricing.actions.buy')} ${definition as string}`}
          onClick={this.handleOnBuyClick}
          rightPart={displayPrice}
          theme={theme}
          widthKind={context === ContextKind.LandscapeTile ? WidthKind.Large : WidthKind.Stretched}
        />
      </div>
    );
  };

  renderButtons = (context: ContextKind, isVodSeries?: boolean): React.Node => {
    const { element: rentElement, isTextOnly: isRentTextOnly } = this.getDisplayData(Purchase.Rent, this.handleOnRentClick);
    const { element: buyElement, isTextOnly: isBuyTextOnly } = this.getDisplayData(Purchase.Buy, this.handleOnBuyClick);

    if (!rentElement && !buyElement) {
      return null;
    }

    return (
      <div
        className={clsx(
          context !== ContextKind.VodEpisode && !isVodSeries && 'pricing purchasable spaced',
          context === ContextKind.VodEpisode && 'episodePricing',
          isBuyTextOnly && !isRentTextOnly && 'reversed',
        )}
        onClick={this.handleOnClick}
      >
        {rentElement}
        {buyElement}
      </div>
    );
  };

  render(): React.Node {
    const { context, expirationTime, isVodSeries, locationsMetadata, purchaseInfo, purchaseList, status } = this.props;
    const {
      minEpisodePrice,
      purchaseDetails: { [Purchase.BuyPack]: packPurchaseDetails },
    } = this.state;
    const lines = [];

    if (status === BOVodAssetStatus.Free) {
      // SVOD: asset is part of the subscription
      lines.push(Localizer.localize('vod.pricing.svod'));
    } else if (status === BOVodAssetStatus.Rented) {
      // TVOD: asset already rented
      lines.push(Localizer.localize('vod.pricing.tvod.rented'));
      if (typeof expirationTime === 'number' && expirationTime > 0) {
        lines.push(Localizer.localize('vod.pricing.tvod.expire', { expiration: getTimeLeftFromExpirationTime(expirationTime) }));
      }
    } else if (status === BOVodAssetStatus.Bought) {
      // EST: asset already bought
      lines.push(Localizer.localize('vod.pricing.est.bought'));
    } else if (packPurchaseDetails && purchaseInfo && purchaseInfo.length > 0) {
      // Purchasable VOD pack
      if (isPackPurchased(purchaseInfo, purchaseList)) {
        return (
          <div className='pricing' onClick={this.handleOnClick}>
            {Localizer.localize('vod.pricing.est.bought_season')}
          </div>
        );
      }

      return this.renderPackPurchaseButton(context);
    } else if (minEpisodePrice) {
      // TVOD or EST series
      lines.push(minEpisodePrice);
    } else if (locationsMetadata && locationsMetadata.length > 0) {
      // TVOD or EST not purchased
      const rentAndBuyButtons = this.renderButtons(context, isVodSeries);
      if (rentAndBuyButtons) {
        return rentAndBuyButtons;
      }
    }

    if (context === ContextKind.VodEpisode) {
      // Purchase status is not displayed here for episodes of VOD series
      return null;
    }

    if (lines.length === 0) {
      // Still loading
      return null;
    }

    return (
      <div className='pricing' onClick={this.handleOnClick}>
        {lines.map((l) => (
          <div key={l}>{l}</div>
        ))}
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxPricingVodReducerStateType => {
  return {
    hideVodPreviewDates: state.appConfiguration.hideVodPreviewDates,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    purchaseList: state.appRegistration.registration === RegistrationType.RegisteredAsGuest ? {} : state.ui.purchaseList,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxPricingVodDispatchToPropsType => {
  return {
    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),
  };
};

const PricingVod: React.ComponentType<PricingVodPropType> = connect(mapStateToProps, mapDispatchToProps)(PricingVodView);

export default PricingVod;
